/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 09 2024
 **/

import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";

import { Utility } from "@/frameworks/libraries/utilities";

type ChatFile = { file: File; isLoading: boolean; id: string; tusID: string; url?: string };

type UseChatInputStateParams = {
	text: string;
	fileMap?: Record<string, ChatFile>;
	emoticon?: { text: string; name?: string };
	forceDisabled?: boolean;
	composition?: boolean;
};

export default function useChatInputState(params: UseChatInputStateParams) {
	const [disabled, setDisabled] = useState(false);
	const [isEmergency, setIsEmergency] = useState(false);

	useEffect(() => {
		if (params.forceDisabled) {
			setDisabled(true);
			return;
		}
		if (params.composition) {
			setDisabled(false);
			return;
		}
		const filesLoaded = !isEmpty(params.fileMap) && Object.values(params.fileMap).every((v) => !v.isLoading);

		const hasEmoticons = params.emoticon;

		let shouldDisable: boolean;

		if (hasEmoticons && isEmpty(params.fileMap)) {
			// 이모티콘만 있을때
			shouldDisable = false;
		} else if (hasEmoticons && !isEmpty(params.fileMap)) {
			// 이모티콘과 파일이 모두 있을때
			shouldDisable = !filesLoaded;
		} else if (!hasEmoticons && !isEmpty(params.fileMap)) {
			// 파일만 있을 때
			shouldDisable = !filesLoaded;
		} else {
			shouldDisable = Utility.removeHtmlTags(params.text).trim().length === 0;
		}
		setDisabled(shouldDisable);
	}, [params]);

	const setEmergency = (emergency: boolean) => {
		setIsEmergency(emergency);
	};

	const actions = {
		setEmergency
	};

	return { disabled, isEmergency, actions };
}
